import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../hooks/useToast';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import { useOfflineSync } from '../../hooks/useOfflineSync';
import { messageCacheService } from '../../services/message-cache.service';
import { messageAdminService, AdminMessage, AdminMessageAttachment } from '../../services/message-admin.service';
import { offlineMessageService } from '../../services/offline-message.service';
import { LoadingState } from '../../components/ui/LoadingState';
import { ErrorState } from '../../components/ui/ErrorState';

function formatDate(ts: number): string {
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Hoy';
  if (d.toDateString() === yesterday.toDateString()) return 'Ayer';
  return d.toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('es-MX', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface PendingBubble {
  id: string;
  content: string;
  createdAt: number;
  attachments?: AdminMessageAttachment[];
  pending: true;
}

type DisplayMessage = (AdminMessage & { pending?: false }) | PendingBubble;

export function ChatPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { isOnline } = useNetworkStatus();
  const { refresh: refreshQueue } = useOfflineSync();

  const [messages, setMessages] = useState<AdminMessage[]>([]);
  const [pending, setPending] = useState<PendingBubble[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState<AdminMessageAttachment[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const endRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await messageCacheService.loadMessages(projectId);
      setMessages(data.messages);
      if (isOnline) {
        try {
          await messageAdminService.markRead(projectId);
        } catch {
          // silent
        }
      }
      const local = await offlineMessageService.listLocalPending(projectId);
      setPending(
        local.map((p) => ({
          id: `pending-${p.created_at}`,
          content: p.content,
          createdAt: p.created_at,
          attachments: p.attachments,
          pending: true,
        }))
      );
    } catch (e: any) {
      setError(e.message || 'Error al cargar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [projectId, isOnline]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, pending]);

  const grouped = useMemo(() => {
    const all: DisplayMessage[] = [
      ...messages,
      ...pending,
    ].sort((a, b) => a.createdAt - b.createdAt);

    const groups: { date: string; messages: DisplayMessage[] }[] = [];
    let current = '';
    for (const m of all) {
      const d = formatDate(m.createdAt);
      if (d !== current) {
        current = d;
        groups.push({ date: d, messages: [] });
      }
      groups[groups.length - 1].messages.push(m);
    }
    return groups;
  }, [messages, pending]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setIsUploading(true);
    try {
      const uploaded: AdminMessageAttachment[] = [];
      for (const file of Array.from(files)) {
        if (file.size > 10 * 1024 * 1024) {
          throw new Error(`${file.name} excede 10MB`);
        }
        const presigned = await messageAdminService.presignUpload({
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
        });
        const res = await fetch(presigned.upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!res.ok) throw new Error(`Error al subir ${file.name}`);
        uploaded.push({
          url: presigned.public_url,
          name: file.name,
          type: file.type,
          size: file.size,
        });
      }
      setAttachments((prev) => [...prev, ...uploaded]);
    } catch (err: any) {
      showError(err.message || 'Error al subir');
    } finally {
      setIsUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSend = async () => {
    if (!projectId) return;
    if (!newMessage.trim() && attachments.length === 0) return;

    setIsSending(true);
    const content = newMessage.trim() || '(adjunto)';
    const atts = attachments.length > 0 ? attachments : undefined;

    try {
      if (isOnline) {
        const sent = await messageAdminService.sendMessage({
          projectId,
          content,
          attachments: atts,
        });
        setMessages((prev) => [...prev, sent]);
      } else {
        await offlineMessageService.saveLocal({
          projectId,
          content,
          attachments: atts,
        });
        await refreshQueue();
        const local = await offlineMessageService.listLocalPending(projectId);
        setPending(
          local.map((p) => ({
            id: `pending-${p.created_at}`,
            content: p.content,
            createdAt: p.created_at,
            attachments: p.attachments,
            pending: true,
          }))
        );
      }
      setNewMessage('');
      setAttachments([]);
      showSuccess(isOnline ? 'Mensaje enviado' : 'Mensaje guardado offline');
    } catch (err: any) {
      showError(err.message || 'Error al enviar');
    } finally {
      setIsSending(false);
    }
  };

  if (loading) return <LoadingState message="Cargando chat…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] -mx-4 -mb-24 max-w-lg mx-auto">
      <div className="bg-white border-b border-slate-200 px-4 py-2 flex items-center gap-2">
        <button
          onClick={() => navigate('/gestion/mensajes')}
          className="text-slate-500 text-lg"
          aria-label="Volver"
        >
          ←
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-500 truncate">Chat del proyecto</p>
          <p className="text-sm font-semibold text-slate-900 truncate">{projectId}</p>
        </div>
        <span
          className={`inline-block w-2 h-2 rounded-full ${
            isOnline ? 'bg-emerald-500' : 'bg-red-500'
          }`}
        />
      </div>

      <div
        className="flex-1 overflow-y-auto px-4 py-3 space-y-4 bg-slate-50"
        style={{ paddingBottom: 'calc(6rem + env(safe-area-inset-bottom))' }}
      >
        {grouped.length === 0 ? (
          <p className="text-center text-sm text-slate-400 py-10">
            No hay mensajes aún.
          </p>
        ) : (
          grouped.map((g) => (
            <div key={g.date}>
              <div className="text-center text-[11px] text-slate-500 mb-2">
                <span className="bg-slate-200 px-2 py-0.5 rounded-full">{g.date}</span>
              </div>
              <div className="space-y-2">
                {g.messages.map((m) => {
                    const isPending = 'pending' in m && m.pending;
                    const isOwn = !isPending && !('isFromClient' in m ? m.isFromClient : false);
                    return (
                      <div
                        key={m.id}
                        className={`flex ${isOwn || isPending ? 'justify-end' : 'justify-start'}`}
                      >
                      <div
                        className={`max-w-[78%] rounded-2xl px-3 py-2 ${
                          isPending
                            ? 'bg-primary/70 text-white'
                            : isOwn
                            ? 'bg-primary text-white'
                            : 'bg-white border border-slate-200 text-slate-800'
                        }`}
                      >
                        {m.content && m.content !== '(adjunto)' && (
                          <p className="text-sm whitespace-pre-wrap break-words">
                            {m.content}
                          </p>
                        )}
                        {'attachments' in m && m.attachments && m.attachments.length > 0 && (
                          <div className="mt-1.5 space-y-1">
                            {m.attachments.map((att, i) => (
                              <a
                                key={i}
                                href={att.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block text-xs underline opacity-90"
                              >
                                📎 {att.name}
                              </a>
                            ))}
                          </div>
                        )}
                        <div
                          className={`text-[10px] mt-1 text-right ${
                            isOwn || isPending ? 'text-white/70' : 'text-slate-400'
                          }`}
                        >
                          {isPending ? '⏳ Pendiente' : formatTime(m.createdAt)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>

      <div
        className="bg-white border-t border-slate-200 px-3 py-2"
        style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
      >
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            {attachments.map((a, i) => (
              <div
                key={i}
                className="bg-slate-100 rounded-md px-2 py-1 text-[11px] flex items-center gap-1"
              >
                <span className="truncate max-w-[100px]">📎 {a.name}</span>
                <button
                  onClick={() =>
                    setAttachments((prev) => prev.filter((_, j) => j !== i))
                  }
                  className="text-red-500"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={isUploading || isSending}
            className="w-10 h-10 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center disabled:opacity-50"
            aria-label="Adjuntar"
          >
            {isUploading ? '⏳' : '📎'}
          </button>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept="image/*,application/pdf,.doc,.docx"
            onChange={handleFileSelect}
            className="hidden"
          />
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={isOnline ? 'Escribe un mensaje…' : 'Mensaje offline…'}
            rows={1}
            disabled={isSending}
            className="flex-1 resize-none px-3 py-2.5 border border-slate-200 rounded-lg focus:border-primary focus:outline-none text-sm max-h-24"
            style={{ minHeight: '40px' }}
          />
          <button
            onClick={handleSend}
            disabled={
              isSending || (!newMessage.trim() && attachments.length === 0)
            }
            className="px-4 h-10 rounded-lg bg-primary text-white font-semibold disabled:opacity-50 flex-shrink-0"
          >
            {isSending ? '⏳' : 'Enviar'}
          </button>
        </div>
      </div>
    </div>
  );
}